import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { pmsAPI } from '../../services/api';
import { exportRowsToExcel, fetchAllRows } from '../../utils/excel';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import './PmsLogs.css';

// The date and time picked, as the server takes them: startDate / endDate in ISO
// (it has no idea of India time).
const withDates = (filters) => {
    const { from, to } = rangeParams({ from: filters.startDate, to: filters.endDate });
    return { ...filters, startDate: from, endDate: to };
};

const PMS_LABELS = {
    capitaland: 'ODX',
    odx:        'ODX',
    winhms:     'WinHMS',
    wincloud:   'WinCloud',
    idsnext:    'IDS Next',
    hotla:      'HOTLA',
    hotelkey:   'HotelKey',
    innkey:     'InnKey',
};

const EVENT_LABELS = {
    portal_submit:     'Portal Submit',
    odx_request:       'ODX Request',
    odx_response:      'ODX Response',
    odx_error:         'ODX Error',
    auth_success:      'Auth Success',
    auth_denied:       'Auth Denied',
    auth_failure:      'Auth Failure',
    auto_rejected:     'Auto Rejected',
    approval_pending:  'Approval Pending',
    approval_approved: 'Approval Approved',
    approval_rejected: 'Approval Rejected',
    session_created:   'Session Created',
};

const EVENT_CLS = {
    auth_success:      'ev-success',
    session_created:   'ev-success',
    approval_approved: 'ev-success',
    auth_denied:       'ev-denied',
    auth_failure:      'ev-error',
    auto_rejected:     'ev-denied',
    approval_rejected: 'ev-denied',
    odx_error:         'ev-error',
    approval_pending:  'ev-pending',
    odx_request:       'ev-info',
    odx_response:      'ev-info',
    portal_submit:     'ev-info',
};

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
}

function fmtMs(ms) {
    if (ms === undefined || ms === null) return '—';
    return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

const METHOD_LABELS = { room_number: 'Room', asr_email: 'ASR' };

// The Excel file: the table's columns (the server pages them 200 at a time).
const EXPORT_COLUMNS = [
    { label: 'Time', field: 'createdAt' },
    { label: 'PMS', value: (r) => PMS_LABELS[r.provider] || r.provider },
    { label: 'Event', value: (r) => EVENT_LABELS[r.event] || r.event },
    { label: 'Room', value: (r) => r.roomNumber || r.roomId },
    { label: 'Last Name', field: 'lastName' },
    { label: 'Method', value: (r) => METHOD_LABELS[r.method] || r.method },
    { label: 'MAC', field: 'deviceMAC' },
    { label: 'Latency (ms)', field: 'latencyMs' },
    { label: 'Allowed', value: (r) => (r.allowed === true ? 'Yes' : r.allowed === false ? 'No' : '') },
    { label: 'Error / Message', value: (r) => r.error || r.odxMessage || (r.pmsDown ? 'PMS Down' : '') },
    { label: 'Correlation ID', field: 'correlationId' },
];

export default function PmsLogs() {
    const navigate = useNavigate();
    const [rows,    setRows]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);

    const [filters, setFilters] = useState({
        provider: '', event: '', allowed: '', roomNumber: '', correlationId: '', startDate: '', endDate: '',
    });

    const limit = 50;

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit, ...withDates(filters) };
            Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null || params[k] === undefined) delete params[k]; });
            const r = await pmsAPI.getActivityLogs(params);
            setRows(r.data.data || []);
            setTotal(r.data.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    function clearFilters() {
        setFilters({ provider: '', event: '', allowed: '', roomNumber: '', correlationId: '', startDate: '', endDate: '' });
        setPage(1);
    }

    // The full guest-authorization timeline lives in AuthorizationSession,
    // keyed by the same correlationId this row carries — resolve it to that
    // document's _id (AuthLogDetail.jsx fetches by _id, not correlationId)
    // rather than duplicating the timeline view here.
    async function viewTimeline(correlationId) {
        try {
            const res = await api.get(`/auth-logs/correlation/${correlationId}`);
            const doc = res.data?.data;
            if (doc?._id) navigate(`/auth-logs/${doc._id}`);
        } catch (_) {}
    }

    // Every entry the filters match (not just this page), up to 10,000, as an Excel file.
    const [exporting, setExporting] = useState(false);
    async function handleExport() {
        setExporting(true);
        try {
            const params = withDates(filters);
            Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null || params[k] === undefined) delete params[k]; });
            const { rows: all, capped } = await fetchAllRows(async ({ page: p, limit: l }) => {
                const r = await pmsAPI.getActivityLogs({ ...params, page: p, limit: l });
                return { rows: r.data.data || [], total: r.data.total };
            }, { pageSize: 200 });
            exportRowsToExcel(`pms-activity-logs-${new Date().toISOString().slice(0, 10)}`, EXPORT_COLUMNS, all, { sheetName: 'PMS Activity Logs' });
            if (capped) toast.info('The first 10,000 entries were exported — narrow the dates or filters for the rest.');
        } catch (_) {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    }

    const pages = Math.ceil(total / limit);

    // Quick totals from current page (for header context when no stats endpoint)
    const allowed  = rows.filter(r => r.allowed === true).length;
    const denied   = rows.filter(r => r.allowed === false).length;
    const avgMs    = rows.filter(r => r.latencyMs).reduce((acc, r) => acc + r.latencyMs, 0) / (rows.filter(r => r.latencyMs).length || 1);

    return (
        <div className="pl-wrap">
            <div className="pl-header">
                <h1 className="pl-title">PMS Activity Logs</h1>
                <button className="pl-refresh" onClick={handleExport} disabled={exporting || total === 0}>
                    {exporting ? 'Exporting…' : '⬇ Export Excel'}
                </button>
                <button className="pl-refresh" onClick={fetchLogs}>↻ Refresh</button>
            </div>

            {/* Quick-stat bar */}
            <div className="pl-cards">
                <StatCard label="Total (page)"   value={rows.length} />
                <StatCard label="Allowed"         value={allowed}     cls="green" />
                <StatCard label="Denied"          value={denied}      cls="red" />
                <StatCard label="Avg Latency"     value={rows.filter(r => r.latencyMs).length ? fmtMs(avgMs) : '—'} />
                <StatCard label="Total in filter" value={total} />
            </div>

            {/* Filters */}
            <div className="pl-filters">
                <select name="provider" value={filters.provider} onChange={handleFilter}>
                    <option value="">All PMS</option>
                    <option value="capitaland">ODX (CapitaLand)</option>
                    <option value="winhms">WinHMS</option>
                    <option value="wincloud">WinCloud</option>
                    <option value="idsnext">IDS Next</option>
                    <option value="hotla">HOTLA</option>
                    <option value="hotelkey">HotelKey</option>
                    <option value="innkey">InnKey</option>
                </select>
                <select name="event" value={filters.event} onChange={handleFilter}>
                    <option value="">All Events</option>
                    <option value="portal_submit">Portal Submit</option>
                    <option value="odx_request">ODX Request</option>
                    <option value="odx_response">ODX Response</option>
                    <option value="odx_error">ODX Error</option>
                    <option value="auth_success">Auth Success</option>
                    <option value="auth_denied">Auth Denied</option>
                    <option value="auth_failure">Auth Failure</option>
                    <option value="auto_rejected">Auto Rejected</option>
                    <option value="approval_pending">Approval Pending</option>
                    <option value="approval_approved">Approval Approved</option>
                    <option value="approval_rejected">Approval Rejected</option>
                    <option value="session_created">Session Created</option>
                </select>
                <select name="allowed" value={filters.allowed} onChange={handleFilter}>
                    <option value="">Allowed: All</option>
                    <option value="true">Allowed</option>
                    <option value="false">Denied</option>
                </select>
                <input name="roomNumber"    placeholder="Room #"         value={filters.roomNumber}    onChange={handleFilter} />
                <input name="correlationId" placeholder="Correlation ID" value={filters.correlationId} onChange={handleFilter} />
                <DateTimeRange
                    value={{ from: filters.startDate, to: filters.endDate }}
                    onChange={({ from, to }) => { setFilters(f => ({ ...f, startDate: from, endDate: to })); setPage(1); }}
                />
                <button className="pl-clear" onClick={clearFilters}>Clear</button>
            </div>

            {/* Table */}
            <div className="pl-table-wrap">
                <table className="pl-table">
                    <thead>
                        <tr>
                            <th className="pl-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>PMS</th>
                            <th>Event</th>
                            <th>Room</th>
                            <th>Last Name</th>
                            <th>Method</th>
                            <th>MAC</th>
                            <th>Latency</th>
                            <th>Allowed</th>
                            <th>Error / Message</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={12} className="pl-loading">Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={12} className="pl-empty">No PMS log entries found</td></tr>
                        )}
                        {!loading && rows.map((row, i) => (
                            <tr key={row._id} className="pl-row">
                                <td className="pl-sr">{(page - 1) * limit + i + 1}</td>
                                <td className="pl-time">{fmt(row.createdAt)}</td>
                                <td>
                                    <span className="pl-pms-badge">
                                        {PMS_LABELS[row.provider] || row.provider || '—'}
                                    </span>
                                </td>
                                <td>
                                    <span className={`pl-event ${EVENT_CLS[row.event] || 'ev-info'}`}>
                                        {EVENT_LABELS[row.event] || row.event || '—'}
                                    </span>
                                </td>
                                <td>{row.roomNumber || row.roomId || '—'}</td>
                                <td>{row.lastName || '—'}</td>
                                <td className="pl-small">{METHOD_LABELS[row.method] || row.method || '—'}</td>
                                <td className="pl-mono pl-small">{row.deviceMAC || '—'}</td>
                                <td className="pl-num">{fmtMs(row.latencyMs)}</td>
                                <td>
                                    {row.allowed === true  && <span className="pl-allowed">✓ Yes</span>}
                                    {row.allowed === false && <span className="pl-denied">✗ No</span>}
                                    {row.allowed === undefined || row.allowed === null ? <span className="pl-na">—</span> : null}
                                </td>
                                <td className="pl-msg">{row.error || row.odxMessage || (row.pmsDown ? 'PMS Down' : '') || '—'}</td>
                                <td>
                                    {row.correlationId
                                        ? <button className="pl-view-btn" onClick={() => viewTimeline(row.correlationId)}>View Timeline</button>
                                        : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pl-pagination">
                <span className="pl-total">Total: {total}</span>
                <button disabled={page <= 1}    onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                <span>Page {page} / {pages || 1}</span>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
        </div>
    );
}

function StatCard({ label, value, cls }) {
    return (
        <div className={`pl-stat-card ${cls || ''}`}>
            <div className="pl-stat-val">{value}</div>
            <div className="pl-stat-label">{label}</div>
        </div>
    );
}
